import React from 'react';
import Icon from '../../../components/AppIcon';

const FeaturesSection = () => {
  const features = [
    {
      id: 1,
      icon: 'MessageSquare',
      title: 'Real-time Messaging',
      description: 'WebSocket-based messaging with automatic reconnection, message queuing, and delivery guarantees.',
      metrics: '< 50ms latency',
      color: 'text-primary'
    },
    {
      id: 2,
      icon: 'Shield',
      title: 'Enterprise Security',
      description: 'End-to-end encryption, SOC 2 compliance, and advanced authentication with role-based access control.',
      metrics: 'SOC 2 Type II',
      color: 'text-accent'
    },
    {
      id: 3,
      icon: 'Zap',
      title: 'Auto-scaling Infrastructure',
      description: 'Automatically scales from 1 to 1M+ concurrent connections with zero configuration required.',
      metrics: '1M+ connections',
      color: 'text-secondary'
    },
    {
      id: 4,
      icon: 'Globe',
      title: 'Global Edge Network',
      description: 'Deploy across 15+ regions worldwide with intelligent routing for optimal performance.',
      metrics: '15+ regions',
      color: 'text-warning'
    },
    {
      id: 5,
      icon: 'Code',
      title: 'Developer-First APIs',
      description: 'RESTful APIs and SDKs for all major platforms with comprehensive documentation and examples.',
      metrics: '10+ SDKs',
      color: 'text-primary'
    },
    {
      id: 6,
      icon: 'BarChart3',
      title: 'Advanced Analytics',
      description: 'Real-time metrics, message tracking, and detailed analytics with custom dashboard creation.',
      metrics: 'Real-time insights',
      color: 'text-accent'
    }
  ];

  return (
    <section className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
            <Icon name="Star" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">
              Enterprise Features
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Everything you need to build
            <span className="block text-primary">messaging at scale</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            From startups to Fortune 500 companies, our infrastructure powers billions of messages 
            with enterprise-grade reliability and developer-friendly APIs.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features?.map((feature) => (
            <div
              key={feature?.id}
              className="group relative bg-card border border-border rounded-xl p-8 hover:border-primary/30 transition-all duration-300 hover:shadow-elevation-3"
            >
              {/* Background Gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              <div className="relative z-10">
                {/* Icon */}
                <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mb-6 group-hover:bg-primary/20 transition-colors duration-300">
                  <Icon name={feature?.icon} size={24} className={feature?.color} />
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold text-foreground mb-3">
                  {feature?.title}
                </h3>
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  {feature?.description}
                </p>

                {/* Metric Badge */}
                <div className="inline-flex items-center space-x-2 bg-muted/50 rounded-full px-3 py-1">
                  <div className="w-2 h-2 bg-accent rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-foreground">
                    {feature?.metrics}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-card border border-border rounded-xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-semibold text-foreground mb-4">
              Ready to get started?
            </h3>
            <p className="text-muted-foreground mb-6">
              Join thousands of developers building the future of communication.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => window.location.href = '/api-documentation'}
                className="inline-flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200"
              >
                <Icon name="FileText" size={18} />
                <span>View Documentation</span>
              </button>
              <button
                onClick={() => window.location.href = '/contact-sales'}
                className="inline-flex items-center space-x-2 px-6 py-3 border border-border rounded-lg text-foreground hover:bg-muted/50 transition-colors duration-200"
              >
                <Icon name="MessageCircle" size={18} />
                <span>Talk to Sales</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;